// cmp/admin/Studio.jsx
import React, { useState, useEffect, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from 'axios';
import { baseUrl } from '../../baseurl';
axios.defaults.baseURL = baseUrl;

const MainStudio = () => {
  const fileInputRef = useRef(null);
  const [importLoading, setImportLoading] = useState(false);
  
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [refresh, setRefresh] = useState(false);
  const [loadingBtn, setLoadingBtn] = useState(false);

  // Reset password modal state
  const [resetPasswordModalOpen, setResetPasswordModalOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Form state
  const [formData, setFormData] = useState({
    "center": "",
    "name": "",
    "email": "",
    "password": "",
    "ip": "",
    "phone": "",
    "role": "teacher",
    "license": "",
    "port_no": "",
    "user_id": "",
    "return_ip": ''
  });

  // Initialize with sample data
  useEffect(() => {
    loadUsers();
  }, [refresh, currentPage, itemsPerPage, searchTerm]);

  const loadUsers = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get('admin/get-main-studio', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params: {
          page: currentPage,
          limit: itemsPerPage,
          search: searchTerm
        }
      });

      if (response.data.success) {
        setUsers(response.data.users || response.data.studio || []);
        setTotalUsers(response.data.total || response.data.users?.length || 0);
        setTotalPages(response.data.totalPages || 1);
      } else {
        toast.error("Failed to fetch users");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error loading users");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const openAddModal = () => {
    setFormData({
      "center": "",
      "name": "",
      "email": "",
      "password": "",
      "ip": "",
      "phone": "",
      "role": "teacher",
      "license": "",
      "port_no": "",
      "user_id": "",
      "return_ip": ''
    });
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setFormData({ 
      center: user.center || '',
      name: user.name || '',
      email: user.email || '',
      ip: user.ip || '',
      phone: user.phone || '',
      role: user.role || 'teacher',
      license: user.license || '',
      port_no: user.port_no || '',
      user_id: user.user_id || '',
      password: '', // Clear password for edit
      return_ip: user.return_ip
    });
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const closeResetPasswordModal = () => {
    setResetPasswordModalOpen(false);
    setSelectedUserId(null);
    setPasswordData({
      password: '',
      confirmPassword: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('vsat_token');
    
    setLoadingBtn(true);
    
    try {
      if (editingUser) {
        // Remove password if empty (don't update password)
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        
        const response = await axios.put(`admin/edit-user/${editingUser._id}`, updateData, {
          headers: {
            Authorization: `Bearer ${token}` 
          }
        });

        if (response.data.success) {
          toast.success("User updated successfully!");
          closeModal();
          setRefresh(!refresh);
        } else {
          toast.error(response.data.message || "Something went wrong!");
        }
      } else {
        const response = await axios.post('admin/save-user', formData, {
          headers: {
            Authorization: `Bearer ${token}` 
          }
        });

        if (response.data.success) {
          toast.success("User registered successfully!");
          closeModal();
          setRefresh(!refresh);
        } else {
          toast.error(response.data.message || "Something went wrong!");
        }
      }
    } catch (error) {
      console.error("Error:", error.response || error);
      toast.error(error.response?.data?.message || error.message || "An error occurred");
    } finally {
      setLoadingBtn(false);
    }
  };

  const deleteUser = async (userId) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        const token = localStorage.getItem("vsat_token");
        const response = await axios.delete(`admin/delete-user/${userId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        });

        if (response.data.success) {
          toast.success("User deleted successfully");
          setRefresh(!refresh);
        } else {
          toast.error("Failed to delete user");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "Error deleting user");
      }
    }
  };

  const toggleUserStatus = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.post(`admin/toggle-user-status/${userId}`, {
        isActive: !currentStatus
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        toast.success(`User ${!currentStatus ? 'activated' : 'deactivated'} successfully`);
        setRefresh(!refresh);
      } else {
        toast.error("Failed to update user status");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error updating user status");
    }
  };

  const getStatusBadge = (isActive) => {
    const status = isActive ? 'active' : 'inactive';
    const styles = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-800',
      teacher: 'bg-blue-100 text-blue-800',
      student: 'bg-green-100 text-green-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[role] || 'bg-gray-100 text-gray-800'}`}>
        {role}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user._id));
    }
  };

  const toggleUserSelection = (userId) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    } else {
      setSelectedUsers([...selectedUsers, userId]);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const openResetPasswordModal = (userId) => {
    setSelectedUserId(userId);
    setPasswordData({
      password: '',
      confirmPassword: ''
    });
    setResetPasswordModalOpen(true);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!passwordData.password || !passwordData.confirmPassword) {
      toast.error("Both fields are required");
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setResetPasswordLoading(true);
    
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.post('admin/user-reset-password', {
        selectedUserId: selectedUserId,
        password: passwordData.password
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        toast.success("Password reset successfully");
        closeResetPasswordModal();
      } else {
        toast.error(response.data.message || "Failed to reset password");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error resetting password");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const exportToCSV = async () => {
    try {
      const token = localStorage.getItem("vsat_token");
      const response = await axios.get("admin/export-users", {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      if (response.data.size === 0) {
        throw new Error('Empty file received');
      }

      if (response.data.type === 'application/json') {
        const errorText = await response.data.text();
        const errorData = JSON.parse(errorText);
        throw new Error(errorData.error || 'Export failed');
      }

      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = `users_${new Date().toISOString().split('T')[0]}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV:", error);
      if (error.response?.data instanceof Blob) {
        try {
          const errorText = await error.response.data.text();
          const errorData = JSON.parse(errorText);
          alert(`Export failed: ${errorData.error || errorData.message || 'Unknown error'}`);
        } catch {
          alert('Export failed: Server error occurred');
        }
      } else {
        alert(`Export failed: ${error.message || 'Please try again'}`);
      }
    }
  };

  const generateLicense = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let license = '';

    for (let i = 0; i < 8; i++) {
      license += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    setFormData(prev => ({
      ...prev,
      license: license
    }));
  };

  const generateUserId = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let user_id = '';

    for (let i = 0; i < 8; i++) {
      user_id += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    setFormData(prev => ({
      ...prev,
      user_id: user_id
    }));
  };

  // Calculate pagination values
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalUsers);

  // Pagination Component
  const Pagination = ({ currentPage, totalPages, onPageChange, itemsPerPage, onItemsPerPageChange, totalItems, showingStart, showingEnd }) => (
    <div className="px-6 py-4 border-t border-gray-200">
      <div className="flex flex-col sm:flex-row items-center justify-between">
        <div className="mb-4 sm:mb-0">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{showingStart}</span> to <span className="font-medium">{showingEnd}</span> of <span className="font-medium">{totalItems}</span> users
          </p>
        </div>
        
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-700">Items per page:</label>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-1 text-sm"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <span className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
            </span>
            
            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Studio Users</h1>
          <p className="text-gray-600">Manage studio users and their access</p>
        </div>
        <div className='flex gap-2'>
          <button
            onClick={openAddModal}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Studio
          </button>

          <button
            onClick={exportToCSV}
            className="flex items-center px-4 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="w-5 h-5 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchTerm}
                onChange={handleSearch}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-6 py-3">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  IP
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  License
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User Id
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Center
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created At
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                    {loading ? 'Loading users...' : 'No users found matching your criteria'}
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user._id)}
                        onChange={() => toggleUserSelection(user._id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white capitalize font-bold">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 capitalize">
                            {user.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getRoleBadge(user.role)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(user.isActive)}
                        <button
                          onClick={() => toggleUserStatus(user._id, user.isActive)}
                          className={`p-1 rounded ${
                            user.isActive 
                              ? 'text-green-600 hover:bg-green-100' 
                              : 'text-red-600 hover:bg-red-100'
                          }`}
                          title={user.isActive ? "Deactivate" : "Activate"}
                        >
                          {user.isActive ? <CheckIcon className="w-4 h-4" /> : <XMarkIcon className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.ip || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.license || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.user_id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      {user.center || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => openResetPasswordModal(user._id)}
                          className="p-2 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                          title="Reset password"
                        >
                          <ArrowPathIcon className="w-4 h-4" />
                        </button>
                        
                        <button
                          onClick={() => deleteUser(user._id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalUsers}
            showingStart={totalUsers === 0 ? 0 : startIndex}
            showingEnd={endIndex}
          />
        )}
      </div>

      {/* Add/Edit User Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingUser ? 'Edit Studio' : 'Add New Studio'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className='grid grid-cols-1 gap-8'>
                <div className='py-4'>
                  <div className="space-y-6">
                    {/* Centre Name */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        Center Name *
                      </label>
                      <div className="flex-1">
                        <input
                          type="text"
                          name="center"
                          value={formData.center}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter centre name"
                        />
                      </div>
                    </div>

                    {/* User Name */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        User Name *
                      </label>
                      <div className="flex-1">
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter user name"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        Email *
                      </label>
                      <div className="flex-1">
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter email address"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        Phone
                      </label>
                      <div className="flex-1">
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter phone number"
                        />
                      </div>
                    </div>

                    {/* Role */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        Role *
                      </label>
                      <div className="flex-1">
                        <select
                          name="role"
                          value={formData.role}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="teacher">Teacher</option>
                          
                        </select>
                      </div>
                    </div>

                    {/* License */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        License *
                      </label>
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          name="license"
                          value={formData.license}
                          onChange={handleInputChange}
                          required
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter Licence code"
                        />
                        <button
                          type="button"
                          onClick={generateLicense}
                          title="Generate Licence"
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* User ID */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        User ID *
                      </label>
                      <div className="flex-1 flex gap-2">
                        <input
                          type="text"
                          name="user_id"
                          value={formData.user_id}
                          onChange={handleInputChange}
                          required
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter User ID"
                        />
                        <button
                          type="button"
                          onClick={generateUserId}
                          title="Generate UserId"
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* IP */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        IP *
                      </label>
                      <div className="flex-1">
                        <input
                          type="text"
                          name="ip"
                          value={formData.ip}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="198.127.11.1"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        Return IP *
                      </label>
                      <div className="flex-1">
                        <input
                          type="text"
                          name="return_ip"
                          value={formData.return_ip}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="198.127.11.1"
                        />
                      </div>
                    </div>

                    {/* Port No */}
                    <div className="flex items-center gap-6">
                      <label className="block text-sm font-medium text-gray-700 w-32">
                        Port No *
                      </label>
                      <div className="flex-1">
                        <input
                          type="number"
                          name="port_no"
                          value={formData.port_no}
                          onChange={handleInputChange}
                          required
                          className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter Port Number"
                        />
                      </div>
                    </div>

                    {/* Password - only required for new users */}
                    {!editingUser && (
                      <div className="flex items-center gap-6">
                        <label className="block text-sm font-medium text-gray-700 w-32">
                          Password *
                        </label>
                        <div className="flex-1">
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required={!editingUser}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter password"
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Password for editing user (optional) */}
                    {editingUser && (
                      <div className="flex items-center gap-6">
                        <label className="block text-sm font-medium text-gray-700 w-32">
                          New Password
                        </label>
                        <div className="flex-1">
                          <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Leave empty to keep current password"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end pt-6 border-t border-gray-200 mt-6">
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    disabled={loadingBtn}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    { loadingBtn ? "Please wait..." : (editingUser ? "UPDATE" : "CREATE") }
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {resetPasswordModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">Reset Password</h3>
                  <p className="text-sm text-gray-500 mt-1">Create a new secure password</p>
                </div>
              </div>
              <button
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                onClick={closeResetPasswordModal}
                disabled={resetPasswordLoading}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleResetPassword}>
              <div className="p-6 space-y-6">
                {/* New Password */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">New Password *</label>
                  <input
                    type="password"
                    name="password"
                    value={passwordData.password}
                    onChange={handlePasswordChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter new password"
                    disabled={resetPasswordLoading}
                  />
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">Confirm Password *</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confirm new password"
                    disabled={resetPasswordLoading}
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex space-x-3 p-6 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                <button
                  type="button"
                  onClick={closeResetPasswordModal}
                  disabled={resetPasswordLoading}
                  className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={resetPasswordLoading}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resetPasswordLoading ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainStudio;